import { useEffect, useState, useCallback } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { fmtDate } from "@/lib/credits";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2, ChevronLeft, ChevronRight, ScrollText } from "lucide-react";

const ACTION_LABEL = (a) => (a || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function AuditLog() {
  const [data, setData] = useState({ rows: [], total: 0, page: 1, page_size: 30, actions: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("all");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 30 };
      if (action !== "all") params.action = action;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/audit-log", { params });
      setData(data);
    } catch (e) {} finally { setLoading(false); }
  }, [page, action, q]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [q, action]);

  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <DashboardLayout title="Audit Log">
      <div className="mb-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Security</p>
        <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">Audit log</h2>
        <p className="mt-1 text-sm text-neutral-500">{data.total} recorded events across the platform.</p>
      </div>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search description or actor…" className="h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] pl-10 text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]" data-testid="audit-search" />
        </div>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="h-11 w-full rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white sm:w-56" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72 border-white/[0.08] bg-[#141414] text-white">
            <SelectItem value="all">All Actions</SelectItem>
            {data.actions.map((a) => (<SelectItem key={a} value={a}>{ACTION_LABEL(a)}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-[#141414]" data-testid="audit-table">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : data.rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center"><ScrollText className="mb-3 h-8 w-8 text-neutral-700" /><p className="text-sm text-neutral-500">No events found.</p></div>
        ) : (
          <div className="divide-y divide-white/[0.05]">
            {data.rows.map((r) => (
              <div key={r.id} className="flex items-start justify-between gap-4 px-6 py-4" data-testid="audit-row">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex rounded-md border border-white/[0.08] bg-[#0A0A0A] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#FF6069]">{ACTION_LABEL(r.action)}</span>
                  <div>
                    <p className="text-sm text-white">{r.description}</p>
                    {r.actor_name && <p className="text-xs text-neutral-600">by {r.actor_name}{r.ip ? ` · ${r.ip}` : ""}</p>}
                  </div>
                </div>
                <p className="whitespace-nowrap text-xs text-neutral-500">{fmtDate(r.created_at, true)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className="text-sm text-neutral-500">Page {data.page} of {totalPages}</p>
        <div className="flex gap-2">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="inline-flex items-center gap-1 rounded-lg border border-white/[0.1] px-3 py-2 text-sm text-neutral-300 hover:bg-white/[0.05] disabled:opacity-40" data-testid="audit-prev"><ChevronLeft className="h-4 w-4" /> Prev</button>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="inline-flex items-center gap-1 rounded-lg border border-white/[0.1] px-3 py-2 text-sm text-neutral-300 hover:bg-white/[0.05] disabled:opacity-40" data-testid="audit-next">Next <ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
    </DashboardLayout>
  );
}
